/**
 * ActiveOrderCard Component
 * Sprint 28: Enhanced with fail-loud errors and report issue button
 * 
 * Shows current active order with status progression
 */

import { useState } from 'react';
import { Order, OrderStatus } from '@/store/orders';
import { formatCurrency } from '@/lib/utils';
import { useRiderStore } from '@/store/rider';
import { ordersApi } from '@/lib/api';
import DeliveryMap from './DeliveryMap';
import ReportIssueModal, { IssueReport, IssueLocation } from './ReportIssueModal';
import { AlertTriangle, Flag } from 'lucide-react';

interface ActiveOrderCardProps {
  order: Order;
  onUpdateStatus: (status: OrderStatus) => Promise<void>;
  isUpdating: boolean;
}

const statusFlow: OrderStatus[] = ['rider_enroute', 'picked_up', 'out_for_delivery', 'delivered'];

const statusLabels: Record<OrderStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  rider_enroute: 'En Route to Pickup',
  picked_up: 'Picked Up',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  // Sprint 28: Issue reported states
  issue_reported: 'Issue Reported',
  awaiting_resolution: 'Awaiting Resolution',
};

const statusActions: Record<OrderStatus, { label: string; next: OrderStatus }> = {
  rider_enroute: { label: 'Mark as Picked Up', next: 'picked_up' },
  accepted: { label: 'Mark as Picked Up', next: 'picked_up' },
  picked_up: { label: 'Start Delivery', next: 'out_for_delivery' },
  out_for_delivery: { label: 'Mark as Delivered', next: 'delivered' },
  delivered: { label: 'Complete', next: 'delivered' },
  pending: { label: 'Accept', next: 'accepted' },
  cancelled: { label: 'Cancelled', next: 'cancelled' },
  // Sprint 28: Blocked states
  issue_reported: { label: 'Awaiting Instructions', next: 'issue_reported' },
  awaiting_resolution: { label: 'Awaiting Resolution', next: 'awaiting_resolution' },
};

export default function ActiveOrderCard({ order, onUpdateStatus, isUpdating }: ActiveOrderCardProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportLocation, setReportLocation] = useState<IssueLocation>('pickup');
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [hasReportedIssue, setHasReportedIssue] = useState(false);
  
  // Get rider's current location
  const riderLocation = useRiderStore((state) => state.currentLocation);
  
  const currentStatusIndex = statusFlow.indexOf(order.status);
  const action = statusActions[order.status];
  const isCompleted = order.status === 'delivered';
  const isCancelled = order.status === 'cancelled';
  
  // Sprint 28: Determine if rider can take actions (not if issue reported)
  const canTakeActions = !hasReportedIssue && !isCompleted && !isCancelled;
  
  // Determine which report button to show based on status
  const reportButtonLocation: IssueLocation = 
    ['rider_enroute', 'accepted'].includes(order.status) ? 'pickup' : 'dropoff';

  const handleNextStatus = async () => {
    if (!action || isUpdating || !canTakeActions) return;
    
    // Sprint 28: Clear any previous error
    setUpdateError(null);
    
    try {
      await onUpdateStatus(action.next);
    } catch (error: any) {
      // Sprint 28: FAIL-LOUD - Show error toast, never silent
      const message = error?.response?.data?.error?.message || 
                      error?.message || 
                      'Failed to update status. Please try again.';
      setUpdateError(message);
      
      // Auto-clear error after 5 seconds
      setTimeout(() => setUpdateError(null), 5000);
    }
  };

  const handleOpenReport = (location: IssueLocation) => {
    setReportLocation(location);
    setShowReportModal(true);
  };

  const handleSubmitReport = async (report: IssueReport) => {
    await ordersApi.reportIssue({
      orderId: report.orderId,
      location: report.location,
      issueType: report.issueType,
      notes: report.notes,
    });
    
    // Mark as having reported an issue - rider cannot complete/cancel
    setHasReportedIssue(true);
    setShowReportModal(false);
  };

  return (
    <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-xl p-4 shadow-lg">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs text-primary font-semibold uppercase mb-1">Active Order</div>
          <h3 className="font-bold text-lg">{order.vendor.name}</h3>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-primary">
            {formatCurrency(order.deliveryFee)}
          </div>
        </div>
      </div>

      {/* Status Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          {statusFlow.map((status, index) => (
            <div key={status} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                index <= currentStatusIndex
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {index < currentStatusIndex ? '✓' : index + 1}
              </div>
              {index < statusFlow.length - 1 && (
                <div className={`flex-1 h-1 mx-1 ${
                  index < currentStatusIndex ? 'bg-primary' : 'bg-muted'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="text-center">
          <div className="text-sm font-medium">{statusLabels[order.status]}</div>
        </div>
      </div>

      {/* Map Toggle */}
      <button
        onClick={() => setShowMap(!showMap)}
        className="w-full text-sm text-primary font-medium mb-2 text-center flex items-center justify-center gap-1"
      >
        {showMap ? '🗺️ Hide Map' : '🗺️ Show Map'}
      </button>

      {/* Delivery Map */}
      {showMap && order.vendor?.location?.lat && order.delivery?.location?.lat && (
        <div className="mb-4">
          <DeliveryMap
            vendorLocation={order.vendor.location}
            buyerLocation={order.delivery.location}
            vendorName={order.vendor.name}
            buyerAddress={order.delivery.address}
            riderLocation={riderLocation}
            orderStatus={order.status}
          />
        </div>
      )}

      {/* Locations */}
      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-3 bg-card p-3 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-xs">
            📍
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground">Pickup</div>
            <div className="text-sm font-medium truncate">{order.vendor.name}</div>
            <div className="text-xs text-muted-foreground truncate">{order.vendor.address}</div>
          </div>
        </div>
        <div className="flex items-start gap-3 bg-card p-3 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center text-white text-xs">
            🏠
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground">Delivery</div>
            <div className="text-sm font-medium truncate">{order.customer.name}</div>
            <div className="text-xs text-muted-foreground truncate">{order.delivery.address}</div>
          </div>
        </div>
      </div>

      {/* Order Details Toggle */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full text-sm text-primary font-medium mb-3 text-center"
      >
        {showDetails ? 'Hide' : 'Show'} Order Details
      </button>

      {/* Expandable Details */}
      {showDetails && (
        <div className="mb-4 space-y-2 bg-card p-3 rounded-lg">
          <div className="text-sm font-semibold mb-2">Items ({order.items.length})</div>
          {order.items.map((item) => (
            <div key={item.id} className="flex justify-between text-sm">
              <span>{item.quantity}× {item.name}</span>
              <span className="font-medium">{formatCurrency(item.price * item.quantity)}</span>
            </div>
          ))}
          <div className="pt-2 border-t flex justify-between font-semibold">
            <span>Total</span>
            <span>{formatCurrency(order.totalAmount)}</span>
          </div>
          <div className="pt-2 flex justify-between text-sm">
            <span className="text-muted-foreground">Payment</span>
            <span className="font-medium">{order.paymentMethod.toUpperCase()}</span>
          </div>
        </div>
      )}

      {/* Sprint 28: Error Display (FAIL-LOUD) */}
      {updateError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-red-800">Update Failed</div>
            <div className="text-sm text-red-700">{updateError}</div>
          </div>
          <button 
            onClick={() => setUpdateError(null)}
            className="text-red-600 hover:text-red-800"
          >
            ✕
          </button>
        </div>
      )}

      {/* Sprint 28: Issue Reported Banner */}
      {hasReportedIssue && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-amber-800">Issue Reported</div>
              <div className="text-sm text-amber-700">
                Awaiting instructions from support. You cannot complete or cancel this order 
                until the issue is resolved.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!isCompleted && !isCancelled && (
        <div className="space-y-2">
          {/* Main Status Action */}
          {canTakeActions && (
            <button
              onClick={handleNextStatus}
              disabled={isUpdating}
              className="w-full py-3 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isUpdating ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Updating...
                </>
              ) : (
                action.label
              )}
            </button>
          )}

          {/* Report Issue Button */}
          {canTakeActions && (
            <button
              onClick={() => handleOpenReport(reportButtonLocation)}
              className="w-full py-3 px-4 border border-amber-300 text-amber-700 bg-amber-50 rounded-lg font-medium hover:bg-amber-100 transition-colors flex items-center justify-center gap-2"
            >
              <Flag className="w-4 h-4" />
              Report Issue at {reportButtonLocation === 'pickup' ? 'Pickup' : 'Dropoff'}
            </button>
          )}
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportModal && (
        <ReportIssueModal
          orderId={order.id}
          location={reportLocation}
          onSubmit={handleSubmitReport}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
}










